import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Spinner from 'src/components/ui/Features/Spinner/Spinner';
import { useDiscover } from 'src/hook/components/useDiscover';
import { useGuides } from 'src/hook/components/useGuides';
import { useCustomList } from 'src/hook/data/useCustomList';
import { useWordList } from 'src/hook/data/useWordList';
import { useLearning } from '../../../hook/data/useLearning';
import useUser from '../../../hook/data/useUser';
import { useWords } from '../../../hook/data/useWords';
import { Direction, DiscoverView, Guides, WordListType } from '../../../model/enum/enums';
import { Word, WordType } from '../../../model/Word';
import { WordList } from '../../../model/WordList';
import styles from './DiscoverPage.module.scss';
import { DiscoverPageHeader } from './DiscoverPageHeader/DiscoverPageHeader';
import KnowledgeMap from './KnowledgeMap/KnowledgeMap';
import ListView from './ListView/ListView';
import { Slider } from './Slider/Slider';
import TagsView from './TagsView/TagsView';

export const DiscoverPage = () => {
    const { getWordsByRange, getWordsByWordList, getWordsByCustomList } = useWords();
    const { userInfo } = useUser();
    const [words, setWords] = useState<Word[]>([]);
    const [filterWords, setFilterWords] = useState<Word[]>([]);
    const { learningInfo } = useLearning();
    const [currentWord, setCurrentWord] = useState<Word>();
    const [currentRange, setCurrentRange] = useState<number>(1);
    const [wordList, setWordList] = useState<WordList>();
    const [selectedView, setSelectedView] = useState<DiscoverView>(DiscoverView.Cards);
    const [forceChangeView, setForceChangeView] = useState<boolean>(false);
    const [searchParams] = useSearchParams();
    const wordListId = searchParams.get('id');
    const range = searchParams.get('range');
    const { fetchWordListData } = useWordList();
    const { fetchCustomListData } = useCustomList();
    const [isViewLoading, setIsViewLoading] = useState<boolean>(false);
    const { updatedRange, setUpdatedRange } = useDiscover();
    const { showGuide } = useGuides();
    const hasShownGuide = useRef(false);

    const fetchWordsByType = useCallback(async (range: number) => {
        if (!wordList) return;

        if (wordList.Type === WordListType.All) {
            return getWordsByRange(range);
        }

        if (wordList.Type === WordListType.Custom) {
            const customData = wordList.CustomListData?.length > 0
                ? wordList.CustomListData
                : await fetchCustomListData(wordList);
            return customData ? getWordsByCustomList(customData, range) : undefined;
        }

        const listData = await fetchWordListData(wordList);
        return listData ? getWordsByWordList(listData, range) : undefined;
    }, [wordList, getWordsByRange, getWordsByCustomList, getWordsByWordList, fetchCustomListData, fetchWordListData]);

    const filterWordsByRange = useCallback(async (range: number, direction?: Direction) => {
        if (range < 1 || range > 243 || !wordList) return;

        const fetchedWords = await fetchWordsByType(range);
        if (!fetchedWords?.length) return;

        // Filter out known and unknown words
        const filteredWords = fetchedWords.filter(word =>
            !learningInfo?.KnownWordIds.includes(word.ID) &&
            !learningInfo?.UnknownWordIds.includes(word.ID)
        );

        // Update view mode based on filtered results
        if (filteredWords.length === 0) {
            if (wordList.IsAll) {
                setSelectedView(DiscoverView.List);
                setForceChangeView(true);
            }
            else {
                if (!direction) direction = Direction.Next;
                const newRange = direction === Direction.Next ? range + 1 : range - 1;
                filterWordsByRange(newRange, direction);
                return;
            }
        } else if (forceChangeView) {
            setForceChangeView(false);
            setSelectedView(DiscoverView.Cards);
        }

        setCurrentRange(range);
        setCurrentWord(filteredWords[0] || fetchedWords[0]);
        setWords(fetchedWords);
        setFilterWords(filteredWords);

    }, [learningInfo, wordList, forceChangeView, fetchWordsByType]);

    const handleRangeChange = useCallback((newRange: number) => {
        const direction = newRange > currentRange ? Direction.Next : Direction.Prev;
        filterWordsByRange(newRange, direction);
    }, [filterWordsByRange, currentRange]);

    const handleViewChange = useCallback((view: DiscoverView) => {
        setIsViewLoading(true);
        // Use setTimeout to allow the loading state to render
        setTimeout(() => {
            setSelectedView(view);
            setIsViewLoading(false);
        }, 0);
    }, []);

    const renderSelectedView = () => {
        if (isViewLoading) {
            return <Spinner />;
        }
        switch (selectedView) {
            case DiscoverView.Cards:
                return <Slider words={filterWords} setCurrentWord={setCurrentWord} goToNextRange={() => handleRangeChange(currentRange + 1)} />;
            case DiscoverView.Tags:
                return <TagsView words={words} setCurrentWord={setCurrentWord} />;
            case DiscoverView.List:
            default:
                return <ListView words={words} setCurrentWord={setCurrentWord} />;
        }
    };


    useEffect(() => {
        if (updatedRange > 0) {
            filterWordsByRange(updatedRange);
            setUpdatedRange(0);
        }
        if (words?.length > 0 || !wordListId) return;

        const wordList = learningInfo?.WordLists.find(x => x.ID === wordListId);
        if (!wordList) return;

        setWordList(wordList);

        const customRange = range || userInfo?.SelectedRange || 1;

        filterWordsByRange(wordList.IsAll ? Number(customRange) : 1);

        if (!hasShownGuide.current) {
            showGuide(Guides.DiscoverFirstVisit);
            hasShownGuide.current = true;
        }

    }, [range, wordListId, filterWordsByRange, setUpdatedRange, showGuide,
        userInfo?.SelectedRange, words?.length, learningInfo?.WordLists, updatedRange]);


    if (!wordList || !words || !filterWords) return <Spinner fullPage />;

    return (
        <article className={styles.discoverPage}>
            <DiscoverPageHeader onViewChange={handleViewChange}
                wordList={wordList!} isFull={forceChangeView} />
            {renderSelectedView()}
            {!isViewLoading && (
                <KnowledgeMap currentWord={currentWord}
                    currentRangeNumber={currentRange} words={words}
                    wordType={wordList?.Title === 'Idioms' ? WordType.Phrase : WordType.Word}
                    onRangeChange={handleRangeChange} />
            )}
        </article>
    );
};